import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Pagination,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface DomainRequest {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  domain: string;
  status: 'pending' | 'in_progress' | 'approved' | 'rejected' | 'contacted';
  message?: string;
  source: 'registration' | 'login';
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  notes: Array<{
    note: string;
    addedBy: {
      _id: string;
      firstName: string;
      lastName: string;
    };
    addedAt: string;
  }>;
  contactedAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminDomainRequests: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<DomainRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [stats, setStats] = useState<any>({});
  
  // Estados para el diálogo de detalles
  const [selectedRequest, setSelectedRequest] = useState<DomainRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Estados para el diálogo de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    status: '',
    note: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  useEffect(() => {
    loadRequests();
  }, [currentPage, searchTerm, statusFilter, domainFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await api.getDomainRequests({
        page: currentPage,
        search: searchTerm,
        status: statusFilter === 'all' ? undefined : statusFilter,
        domain: domainFilter
      });
      
      setRequests(response.requests);
      setTotalPages(response.pagination.total);
      setTotalRequests(response.pagination.totalRecords);
      setStats(response.stats);
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando solicitudes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequest = async (requestId: string) => {
    try {
      setDetailLoading(true);
      const request = await api.getDomainRequest(requestId);
      setSelectedRequest(request);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando detalles',
        severity: 'error'
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditRequest = (request: DomainRequest) => {
    setSelectedRequest(request);
    setEditFormData({
      status: request.status,
      note: ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    try {
      setEditLoading(true);
      await api.updateDomainRequestStatus(
        selectedRequest._id,
        editFormData.status,
        editFormData.note || undefined
      );
      
      setSnackbar({
        open: true,
        message: 'Estado actualizado exitosamente',
        severity: 'success'
      });
      
      setEditDialogOpen(false);
      loadRequests();
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error actualizando estado',
        severity: 'error'
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteRequest = async (request: DomainRequest) => {
    if (!window.confirm(`¿Está seguro de que desea eliminar la solicitud de ${request.firstName} ${request.lastName}?`)) {
      return;
    }

    try {
      await api.deleteDomainRequest(request._id);
      setSnackbar({
        open: true,
        message: 'Solicitud eliminada exitosamente',
        severity: 'success'
      });
      loadRequests();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error eliminando solicitud',
        severity: 'error'
      });
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'in_progress': return 'En Progreso';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'Rechazado';
      case 'contacted': return 'Contactado';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'contacted': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <AccessTimeIcon />;
      case 'in_progress': return <AssignmentIcon />;
      case 'approved': return <CheckCircleIcon />;
      case 'rejected': return <CancelIcon />;
      case 'contacted': return <PhoneIcon />;
      default: return <AccessTimeIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <BusinessIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Solicitudes de Dominio
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona las solicitudes de empresas que desean acceder al sistema
        </Typography>
      </Box>

      {/* Estadísticas */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' },
        gap: 3,
        mb: 3
      }}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {stats.pending || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pendientes
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {stats.in_progress || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              En Progreso
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main">
              {stats.contacted || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contactados
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {stats.approved || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aprobados
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">
              {stats.rejected || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rechazados
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="text.primary">
              {stats.total || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filtros y búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
            alignItems: 'end'
          }}>
            <TextField
              label="Buscar solicitudes"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            
            <TextField
              label="Filtrar por dominio"
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              placeholder="Ej: gmail.com"
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="pending">Pendientes</MenuItem>
                <MenuItem value="in_progress">En Progreso</MenuItem>
                <MenuItem value="contacted">Contactados</MenuItem>
                <MenuItem value="approved">Aprobados</MenuItem>
                <MenuItem value="rejected">Rechazados</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total: {totalRequests} solicitudes
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Tabla de solicitudes */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Solicitante</TableCell>
                  <TableCell>Empresa/Dominio</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No se encontraron solicitudes
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {request.firstName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {request.firstName} {request.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {request.source === 'registration' ? 'Registro' : 'Login'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {request.companyName || 'Sin nombre'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.domain}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {request.email}
                            </Typography>
                          </Box>
                          {request.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {request.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(request.status)}
                          label={getStatusLabel(request.status)}
                          color={getStatusColor(request.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Ver detalles">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewRequest(request._id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar estado">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEditRequest(request)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteRequest(request)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de detalles */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PersonIcon />
            <Typography variant="h6">
              Detalles de la Solicitud
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Cargando detalles...</Typography>
            </Box>
          ) : selectedRequest ? (
            <Box>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3
              }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Información del Solicitante
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nombre Completo
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.firstName} {selectedRequest.lastName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.email}
                    </Typography>
                  </Box>
                  {selectedRequest.phone && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Teléfono
                      </Typography>
                      <Typography variant="body1">
                        {selectedRequest.phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Información de la Empresa
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nombre de la Empresa
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.companyName || 'No especificado'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Dominio
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.domain}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Estado
                    </Typography>
                    <Chip
                      icon={getStatusIcon(selectedRequest.status)}
                      label={getStatusLabel(selectedRequest.status)}
                      color={getStatusColor(selectedRequest.status) as any}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              {selectedRequest.message && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Mensaje
                  </Typography>
                  <Typography variant="body1">
                    {selectedRequest.message}
                  </Typography>
                </Box>
              )}

              {selectedRequest.notes && selectedRequest.notes.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Notas
                  </Typography>
                  {selectedRequest.notes.map((note, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2">
                        {note.note}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Por {note.addedBy.firstName} {note.addedBy.lastName} - {formatDate(note.addedAt)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            CERRAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de edición */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Actualizar Estado de Solicitud
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                label="Estado"
              >
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="in_progress">En Progreso</MenuItem>
                <MenuItem value="contacted">Contactado</MenuItem>
                <MenuItem value="approved">Aprobado</MenuItem>
                <MenuItem value="rejected">Rechazado</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Nota (opcional)"
              value={editFormData.note}
              onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
              multiline
              rows={3}
              fullWidth
              placeholder="Agregar una nota sobre el cambio de estado..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditDialogOpen(false)} 
            disabled={editLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDomainRequests;
